import ShopifyLinkPage from './pages/ShopifyLinkPage';
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useSearchParams, useLocation, useParams } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { HelmetProvider } from 'react-helmet-async';
import { CurrencyProvider } from './contexts/CurrencyContext';
import ErrorBoundary from './components/ErrorBoundary';

// Build version: 2026-03-06-v5 - Order Orchestration, WISMO, Agent Work Hub, Settlement, Sourcing/Allocation Engines
const BUILD_VERSION = '2026-03-06-v5';

// Merchant Pages
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import ShopifyLogin from './pages/ShopifyLogin';
import Authenticate from './pages/Authenticate';
import Dashboard from './pages/Dashboard';
import TwitterAgentPage from './pages/TwitterAgentPage';
import DashboardDeals from './pages/DashboardDeals';
import MerchantSettings from './pages/MerchantSettings';
import AnalyticsPage from './pages/AnalyticsPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CreatorNetworkPage from './pages/CreatorNetworkPage';
import AffiliateRevenuePage from './pages/AffiliateRevenuePage';
import PricingIntelligencePage from './pages/PricingIntelligencePage';
import PerformancePage from './pages/PerformancePage';
import DistributionEnginePage from './pages/DistributionEnginePage';
import FeedbackEnginePage from './pages/FeedbackEnginePage';
import ProductEnginePage from './pages/ProductEnginePage';
import ConversionInsightsPage from './pages/ConversionInsightsPage';
import AIRecommendationsPage from './pages/AIRecommendationsPage';
import ShopifyIntegrationPage from './pages/ShopifyIntegrationPage';
import MerchantManagement from './pages/MerchantManagement';
import DeveloperAPIPage from './pages/DeveloperAPIPage';
import BillingDashboardPage from './pages/BillingDashboardPage';
import SubscriptionPage from './pages/SubscriptionPage';
import TeamAlertsPage from './pages/TeamAlertsPage';
import ShopifyEmbeddedApp from './pages/ShopifyEmbeddedApp';
import PromiseHealthPage from './pages/PromiseHealthPage';
import MarginGuardPage from './pages/MarginGuardPage';
import MarginGuardOrdersPage from './pages/MarginGuardOrdersPage';
import PromiseWizardPage from './pages/PromiseWizardPage';
import ExceptionRadarPage from './pages/ExceptionRadarPage';
import SlackSettingsPage from './pages/SlackSettingsPage';
import ApprovalQueuePage from './pages/ApprovalQueuePage';
import SlackDigestDeliveriesPage from './pages/SlackDigestDeliveriesPage';
import OnboardingWizardPage from './pages/OnboardingWizardPage';
import ProofSetupPage from './pages/ProofSetupPage';
import ShoppersPage from './pages/ShoppersPage';
import CheckoutPage from './pages/CheckoutPage';
import PoliciesPage from './pages/PoliciesPage';
import PlaybooksPage from './pages/PlaybooksPage';
import ArchitecturePage from './pages/ArchitecturePage';
import PortfolioDashboardPage from './pages/PortfolioDashboardPage';
import CostEstimatorPage from './pages/CostEstimatorPage';
import ThreePLMarketplacePage from './pages/ThreePLMarketplacePage';
import TranscriptViewerPage from './pages/TranscriptViewerPage';
import CreatorDashboardPage from './pages/CreatorDashboardPage';
import EnginesPage from './pages/EnginesPage';
import CortexPage from './pages/CortexPage';
import CortexPreorderSuccess from './pages/CortexPreorderSuccess';
import DocsIntegrationMap from './pages/docs/integrations/DocsIntegrationMap';
import DocsAmazonSeller from './pages/docs/integrations/marketplaces/DocsAmazonSeller';
import DocsAmazonVendor from './pages/docs/integrations/marketplaces/DocsAmazonVendor';
import DocsWalmart from './pages/docs/integrations/marketplaces/DocsWalmart';
import DocsEbay from './pages/docs/integrations/marketplaces/DocsEbay';
import DocsEtsy from './pages/docs/integrations/marketplaces/DocsEtsy';
import DocsGoogleShopping from './pages/docs/integrations/marketplaces/DocsGoogleShopping';
import DocsTargetPlus from './pages/docs/integrations/marketplaces/DocsTargetPlus';
import DocsBigCommerce from './pages/docs/integrations/platforms/DocsBigCommerce';
import DocsWooCommerce from './pages/docs/integrations/platforms/DocsWooCommerce';
import DocsMagento from './pages/docs/integrations/platforms/DocsMagento';
import DocsSquarespace from './pages/docs/integrations/platforms/DocsSquarespace';
import DocsWMSOverview from './pages/docs/integrations/warehousing/DocsWMSOverview';
import DocsShipBob from './pages/docs/integrations/warehousing/DocsShipBob';
import DocsShipMonk from './pages/docs/integrations/warehousing/DocsShipMonk';
import DocsShipHero from './pages/docs/integrations/warehousing/DocsShipHero';
import DocsAmazonMCF from './pages/docs/integrations/warehousing/DocsAmazonMCF';
import DocsFlexport from './pages/docs/integrations/warehousing/DocsFlexport';
import Docs3PLOnboarding from './pages/docs/integrations/warehousing/Docs3PLOnboarding';
import DocsNetSuite from './pages/docs/integrations/oms/DocsNetSuite';
import DocsManhattan from './pages/docs/integrations/oms/DocsManhattan';
import DashboardDemandOffersPage from './pages/DashboardDemandOffersPage';
import FundingBudgetPage from './pages/FundingBudgetPage';
import DashboardExperimentsPage from './pages/DashboardExperimentsPage';
import FinancialHealthPage from './pages/FinancialHealthPage';

// Phase 1: Launch a Store
import StorefrontBuilderPage from './pages/StorefrontBuilderPage';
import CatalogManagerPage from './pages/CatalogManagerPage';
import PaymentGatewayPage from './pages/PaymentGatewayPage';
import OrderManagementPage from './pages/OrderManagementPage';

// Phase 2: Agentic Operations
import AgentCommandCenterPage from './pages/AgentCommandCenterPage';
import AgentMarketplacePage from './pages/AgentMarketplacePage';
import InventoryManagementPage from './pages/InventoryManagementPage';
import FulfillmentPage from './pages/FulfillmentPage';

// Phase 3: Intelligence Layer
import IntelligenceHubPage from './pages/IntelligenceHubPage';
import MemoryAnalyticsPage from './pages/MemoryAnalyticsPage';
import SEOManagementPage from './pages/SEOManagementPage';

// Phase 4: Scale & Grow
import FundingDashboardPage from './pages/FundingDashboardPage';
import GlobalCommercePage from './pages/GlobalCommercePage';
import SecurityCompliancePage from './pages/SecurityCompliancePage';
import ChannelsHubPage from './pages/ChannelsHubPage';

// Phase 5: Enterprise Commerce Engines
import OrderOrchestrationPage from './pages/OrderOrchestrationPage';
import WISMOPage from './pages/WISMOPage';
import AgentWorkHubPage from './pages/AgentWorkHubPage';

// Phase 6: CMO Agent + M2M Lending
import CMODashboardPage from './pages/CMODashboardPage';
import MerchantLendingPage from './pages/MerchantLendingPage';

// Phase 7: Agent Org Chart
import AgentOrgChartPage from './pages/AgentOrgChartPage';

// Phase 8: Brand IP + Corp Compliance (Trademark, RA, State filings)
import TrademarkSearchPage from './pages/TrademarkSearchPage';

// Phase K: Docs graph (wikilink-derived visualization)
import DocsGraphPage from './pages/DocsGraphPage';

// Admin Pages
import AdminLogin from './pages/admin/AdminLogin';
import SuperAdminDashboard from './pages/admin/SuperAdminDashboard';
import MerchantAdminDashboard from './pages/admin/MerchantAdminDashboard';
import BlogAdmin from './pages/admin/BlogAdmin';

// Conversational Commerce Chat
import ChatPanel from './components/chat/ChatPanel';
import ChatSettingsPage from './pages/ChatSettingsPage';

/**
 * AuthenticatedChatPanel — only mounts <ChatPanel /> when the user has a
 * session token. Prevents the public homepage from showing an "Authentication
 * required" error to anonymous visitors. The hero CTA ("Text us to start")
 * already covers the entry point for logged-out users.
 */
function AuthenticatedChatPanel() {
  const [hasSession, setHasSession] = React.useState(
    () => !!sessionStorage.getItem('session_token')
  );
  React.useEffect(() => {
    const onStorage = () => setHasSession(!!sessionStorage.getItem('session_token'));
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
  if (!hasSession) return null;
  return <ChatPanel />;
}

// Marketing & Landing
import LandingPage from './pages/LandingPage';
import WaitlistStatusPage from './pages/WaitlistStatusPage';
import IntegrationsPage from './pages/IntegrationsPage';
import StoriesPage from './pages/StoriesPage';
import BusinessOSPage from './pages/BusinessOSPage';
// LandingPageV1 archive removed — single LandingPage is the live homepage
// Feature flags available via: import { useFeatureFlag } from './config/featureFlags';
import APIDocsPage from './pages/APIDocsPage';
import CheckoutSuccess from './pages/CheckoutSuccess';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import ContactUs from './pages/ContactUs';
import AboutUs from './pages/AboutUs';
import BlogPage from './pages/BlogPage';
import DevelopersPage from './pages/DevelopersPage';
import BrandProofPage from './pages/BrandProofPage';
import SignalsPage from './pages/SignalsPage';
import ExtensionPage from './pages/ExtensionPage';
import ExtensionSuccess from './pages/ExtensionSuccess';
import ExtensionCancel from './pages/ExtensionCancel';
import BrandsPage from './pages/BrandsPage';
import AgentOnboardingPage from './pages/AgentOnboardingPage';
import WhatIsCommerceIndexPage from './pages/WhatIsCommerceIndexPage';

// Documentation Pages
import {
  DocsLayout,
  DocsOverview,
  DocsQuickStart,
  DocsEvents,
  DocsJSAPI,
  DocsRestAPI,
  DocsExamples,
  DocsFAQ,
  DocsSignals,
  DocsProofIntegration,
  DocsShopperGuide,
  DocsPromiseHealth,
  DocsExceptionRadar,
  DocsApprovalQueue,
  DocsSlackIntegration,
  DocsWelcome,
  DocsGettingStartedDecisionTree,
  DocsOnboardingChecklist,
  DocsShopifySetup,
  DocsShopifyEmbeddedApp,
  DocsShopifySync,
  DocsManualSetup,
  DocsAPIIntegration,
  DocsWebhookSetup,
  DocsOpenClawOverview,
  DocsChannelSetup,
  DocsMessagingCommands,
  DocsAccountLinking,
  DocsDailyBriefs,
  DocsChannelsSignup,
  DocsAgentRoster,
  DocsAgentsOverview,
  DocsTrustLadder,
  DocsGuardrails,
  DocsAgentAdmin,
  DocsMacMiniSetup,
  DocsLocalLLM,
  DocsAPIReference
} from './pages/docs';
import DocsCIScore from './pages/docs/DocsCIScore';

// Consumer Pages
import ConsumerHome from './pages/consumer/Home';
// DEPRECATED: CI Deals page - now using TheDeals.ai
// import DealsPage from './pages/consumer/DealsPage';
// import DealDetailPage from './pages/consumer/DealDetailPage';
import ConsumerAuth from './pages/consumer/ConsumerAuth';
import ConsumerAuthCallback from './pages/consumer/ConsumerAuthCallback';
import WatchlistPage from './pages/consumer/WatchlistPage';
import PreferencesPage from './pages/consumer/PreferencesPage';

/**
 * Redirect /deal/:dealId to TheDeals.ai with the deal ID preserved.
 * Old CI consumer links like /deal/abc123 → https://thedeals.ai/ci/deal/abc123
 */
const DealRedirectToTheDeals = () => {
  const { dealId } = useParams();
  React.useEffect(() => {
    window.location.replace(`https://thedeals.ai/ci/deal/${dealId}`);
  }, [dealId]);
  return null;
};

/**
 * Redirect /deals to TheDeals.ai homepage with attribution.
 * React Router Navigate doesn't work for external URLs, so we use window.location.
 */
const DealsRedirectToTheDeals = () => {
  React.useEffect(() => {
    window.location.replace('https://thedeals.ai/?utm_source=commerceindex');
  }, []);
  return null;
};

/**
 * /pricing → landing page pricing section. Upgrade banners and external
 * links point here; without this route it 404s.
 */
const PricingRedirect = () => {
  React.useEffect(() => {
    window.location.replace('/#pricing');
  }, []);
  return null;
};

/**
 * Helper to detect Shopify embedded context
 * Checks URL params for 'host' which Shopify injects into embedded app URLs
 */
const isShopifyEmbeddedContext = () => {
  const params = new URLSearchParams(window.location.search);
  return params.has('host');
};

/**
 * Protected Route Component for External Merchant Dashboard
 * ONLY for external portal - redirects to /login if not authenticated
 * 
 * CRITICAL: This must NEVER be used for Shopify embedded routes.
 * Shopify embedded routes use Shopify session tokens, not cookie sessions.
 */
const ProtectedRoute = ({ children }) => {
  // Accept both auth flavors: Stytch web sessions (sessionStorage) and
  // phone-signup sessions (localStorage ci_session_token)
  const token = sessionStorage.getItem('session_token')
    || localStorage.getItem('ci_session_token');

  // SAFETY CHECK: If we somehow end up in a protected route with Shopify context,
  // redirect to the embedded app instead of login (prevents 2.2.2 violation)
  if (isShopifyEmbeddedContext()) {
    console.warn('[CI] ProtectedRoute accessed with Shopify host param - redirecting to embedded app');
    return <Navigate to={`/app${window.location.search}`} replace />;
  }

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

/**
 * AppRouter - Main routing component with Shopify embedded context detection
 * 
 * CRITICAL ROUTING LOGIC:
 * 1. If URL has 'host' param (Shopify Admin context) → Route to ShopifyEmbeddedApp ONLY
 * 2. If URL has session_id in hash (OAuth callback) → Route to AuthCallback
 * 3. Otherwise → Normal external app routing
 * 
 * This ensures Shopify merchants NEVER see /login or Google OAuth inside the Admin iframe.
 */
function LandingPageSwitch() {
  return <LandingPage />;
}

function AppRouter() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Check for Shopify embedded context FIRST (synchronous, during render)
  const hasHostParam = searchParams.has('host');
  const isEmbeddedPath = location.pathname === '/app' || location.pathname === '/shopify/embedded';
  // Standalone flow pages that must render even when a host param is present
  // (Shopify deep-link or App Bridge force-embed): the account-link popup and
  // the magic-link auth return. Silently redirecting these to /app loses the
  // opener handshake and the auth token exchange; the link page shows its own
  // graceful no-opener state when it ends up inside an iframe.
  const isLinkFlowPath = location.pathname === '/shopify-link' || location.pathname === '/merchant/authenticate';
  
  // If we have host param but NOT on embedded path, redirect to embedded app
  // This catches cases where Shopify might deep-link to other paths with host param
  if (hasHostParam && !isEmbeddedPath && !isLinkFlowPath) {
    return <Navigate to={`/app${location.search}`} replace />;
  }
  
  // Check for OAuth callback (session_id in URL hash)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  // Return null to render normal routes
  return null;
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <CurrencyProvider>
          <AppRouterWrapper />
        </CurrencyProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

/**
 * AppRouterWrapper - Wraps routes with context detection
 */
function AppRouterWrapper() {
  const routerResult = AppRouter();
  
  // If AppRouter returns a component (redirect or callback), render that
  if (routerResult) {
    return routerResult;
  }
  
  // Otherwise render normal routes
  return (
    <>
      <Toaster
        position="top-right"
        theme="dark"
        richColors
        toastOptions={{
          className: 'sonner-toast',
        }}
      />
      <AuthenticatedChatPanel />
      <ErrorBoundary fallbackTitle="Something went wrong">
      <Routes>
        {/* Marketing Landing Page — feature flag kill switch */}
        <Route path="/" element={<LandingPageSwitch />} />
        <Route path="/pricing" element={<PricingRedirect />} />
        <Route path="/status/:code" element={<WaitlistStatusPage />} />
        <Route path="/integrations" element={<IntegrationsPage />} />
        <Route path="/stories" element={<StoriesPage />} />
        <Route path="/dashboard/business-os" element={<BusinessOSPage />} />
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/checkout/success" element={<CheckoutSuccess />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/contact" element={<ContactUs />} />
        <Route path="/about" element={<AboutUs />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/support" element={<ContactUs />} />
        <Route path="/developers" element={<DevelopersPage />} />
        <Route path="/proof" element={<BrandProofPage />} />
        <Route path="/signals" element={<SignalsPage />} />
        <Route path="/extension" element={<ExtensionPage />} />
        <Route path="/extension/success" element={<ExtensionSuccess />} />
        <Route path="/extension/cancel" element={<ExtensionCancel />} />
        <Route path="/brands" element={<BrandsPage />} />
        <Route path="/agent-onboarding" element={<AgentOnboardingPage />} />
        <Route path="/what-is-commerce-index" element={<WhatIsCommerceIndexPage />} />
        <Route path="/shoppers" element={<ShoppersPage />} />
        <Route path="/cortex" element={<CortexPage />} />
        <Route path="/cortex/preorder/success" element={<CortexPreorderSuccess />} />
        
        {/* Documentation Routes */}
        <Route path="/docs" element={<DocsLayout />}>
          <Route index element={<DocsOverview />} />
          <Route path="quickstart" element={<DocsQuickStart />} />
          <Route path="proof-integration" element={<DocsProofIntegration />} />
          <Route path="shopper-guide" element={<DocsShopperGuide />} />
          <Route path="events" element={<DocsEvents />} />
          <Route path="js-api" element={<DocsJSAPI />} />
          <Route path="rest-api" element={<DocsRestAPI />} />
          <Route path="examples" element={<DocsExamples />} />
          <Route path="faq" element={<DocsFAQ />} />
          <Route path="signals" element={<DocsSignals />} />
          <Route path="ci-score" element={<DocsCIScore />} />
          <Route path="promise-health" element={<DocsPromiseHealth />} />
          <Route path="exception-radar" element={<DocsExceptionRadar />} />
          <Route path="approval-queue" element={<DocsApprovalQueue />} />
          <Route path="slack-integration" element={<DocsSlackIntegration />} />
          <Route path="welcome" element={<DocsWelcome />} />
          <Route path="getting-started" element={<DocsGettingStartedDecisionTree />} />
          <Route path="onboarding-checklist" element={<DocsOnboardingChecklist />} />
          <Route path="shopify-setup" element={<DocsShopifySetup />} />
          <Route path="shopify-embedded" element={<DocsShopifyEmbeddedApp />} />
          <Route path="shopify-sync" element={<DocsShopifySync />} />
          <Route path="manual-setup" element={<DocsManualSetup />} />
          <Route path="api-integration" element={<DocsAPIIntegration />} />
          <Route path="webhook-setup" element={<DocsWebhookSetup />} />
          <Route path="openclaw" element={<DocsOpenClawOverview />} />
          <Route path="channel-setup" element={<DocsChannelSetup />} />
          <Route path="commands" element={<DocsMessagingCommands />} />
          <Route path="account-linking" element={<DocsAccountLinking />} />
          <Route path="daily-briefs" element={<DocsDailyBriefs />} />
          <Route path="channels-signup" element={<DocsChannelsSignup />} />
          <Route path="agent-roster" element={<DocsAgentRoster />} />
          <Route path="agents" element={<DocsAgentsOverview />} />
          <Route path="trust-ladder" element={<DocsTrustLadder />} />
          <Route path="guardrails" element={<DocsGuardrails />} />
          <Route path="agent-admin" element={<DocsAgentAdmin />} />
          <Route path="mac-mini" element={<DocsMacMiniSetup />} />
          <Route path="local-llm" element={<DocsLocalLLM />} />
          <Route path="api-reference" element={<DocsAPIReference />} />
        </Route>
        <Route path="/api-docs" element={<APIDocsPage />} />
        
        {/* Consumer Routes (Public) */}
        {/* DEPRECATED: CI Deals pages — all consumer traffic redirected to TheDeals.ai */}
        <Route path="/deals" element={<DealsRedirectToTheDeals />} />
        <Route path="/deal/:dealId" element={<DealRedirectToTheDeals />} />
        <Route path="/auth" element={<ConsumerAuth />} />
        <Route path="/consumer/authenticate" element={<ConsumerAuthCallback />} />
        <Route path="/watchlist" element={<WatchlistPage />} />
        <Route path="/preferences" element={<PreferencesPage />} />
        
        {/* Shopify Embedded App - runs inside Shopify Admin iframe */}
        <Route path="/shopify/embedded" element={<ShopifyEmbeddedApp />} />
        <Route path="/app" element={<ShopifyEmbeddedApp />} />
        
        {/* Merchant Routes */}
        <Route path="/merchant/login" element={<Login />} />
        <Route path="/merchant/shopify" element={<ShopifyLogin />} />
        <Route path="/shopify-link" element={<ShopifyLinkPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/merchant/authenticate" element={<Authenticate />} />
        <Route path="/authenticate" element={<Authenticate />} />
        <Route path="/auth/callback" element={<AuthCallback />} />

        {/* Onboarding Wizard */}
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              <OnboardingWizardPage />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        {/* DEPRECATED: Merchant Deals pages - deals now publish directly to TheDeals.ai */}
        {/*
        <Route
          path="/dashboard/deals"
          element={
            <ProtectedRoute>
              <DashboardDeals />
            </ProtectedRoute>
          }
        />
        */}
        <Route
          path="/dashboard/deals"
          element={
            <ProtectedRoute>
              <Navigate to="/dashboard" replace />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/deals/demand"
          element={
            <ProtectedRoute>
              <DashboardDemandOffersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/proof/setup"
          element={
            <ProtectedRoute>
              <ProofSetupPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/deals/performance"
          element={
            <ProtectedRoute>
              <PerformancePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/deals/distribution"
          element={
            <ProtectedRoute>
              <DistributionEnginePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/feedback"
          element={
            <ProtectedRoute>
              <FeedbackEnginePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/products/engine"
          element={
            <ProtectedRoute>
              <ProductEnginePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/settings"
          element={
            <ProtectedRoute>
              <MerchantSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/twitter"
          element={
            <ProtectedRoute>
              <TwitterAgentPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/shopify"
          element={
            <ProtectedRoute>
              <ShopifyIntegrationPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/analytics"
          element={
            <ProtectedRoute>
              <AnalyticsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/analytics/conversion"
          element={
            <ProtectedRoute>
              <ConversionInsightsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/product/:productId"
          element={
            <ProtectedRoute>
              <ProductDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/creators"
          element={
            <ProtectedRoute>
              <CreatorNetworkPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/affiliate"
          element={
            <ProtectedRoute>
              <AffiliateRevenuePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/pricing-intelligence"
          element={
            <ProtectedRoute>
              <PricingIntelligencePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/recommendations"
          element={
            <ProtectedRoute>
              <AIRecommendationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/admin/merchants"
          element={
            <ProtectedRoute>
              <MerchantManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/developers"
          element={
            <ProtectedRoute>
              <DeveloperAPIPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/team"
          element={
            <ProtectedRoute>
              <TeamAlertsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/settings/billing"
          element={
            <ProtectedRoute>
              <BillingDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/settings/billing/subscription"
          element={
            <ProtectedRoute>
              <SubscriptionPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/promise-health"
          element={
            <ProtectedRoute>
              <PromiseHealthPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/margin-guard"
          element={
            <ProtectedRoute>
              <MarginGuardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/intelligence/margin-guard"
          element={
            <ProtectedRoute>
              <MarginGuardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/margin-guard/orders"
          element={
            <ProtectedRoute>
              <MarginGuardOrdersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/margin-guard/traces/:traceId"
          element={
            <ProtectedRoute>
              <MarginGuardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/settings/policies"
          element={
            <ProtectedRoute>
              <PoliciesPage />
            </ProtectedRoute>
          }
        />
        <Route path="/playbooks" element={<PlaybooksPage />} />
        <Route path="/architecture" element={<ArchitecturePage />} />
        <Route path="/portfolio" element={<PortfolioDashboardPage />} />
        <Route path="/portfolio/new" element={<CostEstimatorPage />} />
        {/* Legacy deep-link alias: old signup redirects pointed here */}
        <Route path="/portfolio/settings/billing" element={<Navigate to="/dashboard/settings/billing/subscription" replace />} />
        <Route path="/estimate" element={<CostEstimatorPage />} />
        <Route path="/3pl" element={<ThreePLMarketplacePage />} />
        <Route path="/transcripts/:transcriptId" element={<TranscriptViewerPage />} />
        <Route path="/creator" element={<CreatorDashboardPage />} />
        <Route path="/engines" element={<EnginesPage />} />
        <Route path="/docs/integrations" element={<DocsIntegrationMap />} />
        <Route path="/docs/integrations/marketplaces/amazon-seller" element={<DocsAmazonSeller />} />
        <Route path="/docs/integrations/marketplaces/amazon-vendor" element={<DocsAmazonVendor />} />
        <Route path="/docs/integrations/marketplaces/walmart" element={<DocsWalmart />} />
        <Route path="/docs/integrations/marketplaces/ebay" element={<DocsEbay />} />
        <Route path="/docs/integrations/marketplaces/etsy" element={<DocsEtsy />} />
        <Route path="/docs/integrations/marketplaces/google-shopping" element={<DocsGoogleShopping />} />
        <Route path="/docs/integrations/marketplaces/target-plus" element={<DocsTargetPlus />} />
        <Route path="/docs/integrations/platforms/bigcommerce" element={<DocsBigCommerce />} />
        <Route path="/docs/integrations/platforms/woocommerce" element={<DocsWooCommerce />} />
        <Route path="/docs/integrations/platforms/magento" element={<DocsMagento />} />
        <Route path="/docs/integrations/platforms/squarespace" element={<DocsSquarespace />} />
        <Route path="/docs/integrations/warehousing/wms-overview" element={<DocsWMSOverview />} />
        <Route path="/docs/integrations/warehousing/shipbob" element={<DocsShipBob />} />
        <Route path="/docs/integrations/warehousing/shipmonk" element={<DocsShipMonk />} />
        <Route path="/docs/integrations/warehousing/shiphero" element={<DocsShipHero />} />
        <Route path="/docs/integrations/warehousing/amazon-mcf" element={<DocsAmazonMCF />} />
        <Route path="/docs/integrations/warehousing/flexport" element={<DocsFlexport />} />
        <Route path="/docs/integrations/warehousing/3pl-onboarding" element={<Docs3PLOnboarding />} />
        <Route path="/docs/integrations/oms/netsuite" element={<DocsNetSuite />} />
        <Route path="/docs/integrations/oms/manhattan" element={<DocsManhattan />} />
        <Route
          path="/dashboard/settings/promise-wizard"
          element={
            <ProtectedRoute>
              <PromiseWizardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/exception-radar"
          element={
            <ProtectedRoute>
              <ExceptionRadarPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/settings/integrations/slack"
          element={
            <ProtectedRoute>
              <SlackSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/settings/integrations/slack/deliveries"
          element={
            <ProtectedRoute>
              <SlackDigestDeliveriesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/approvals"
          element={
            <ProtectedRoute>
              <ApprovalQueuePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/funding/budget"
          element={
            <ProtectedRoute>
              <FundingBudgetPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/experiments"
          element={
            <ProtectedRoute>
              <DashboardExperimentsPage />
            </ProtectedRoute>
          }
        />
        
        {/* Phase 1: Launch a Store */}
        <Route
          path="/dashboard/storefront"
          element={
            <ProtectedRoute>
              <StorefrontBuilderPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/catalog"
          element={
            <ProtectedRoute>
              <CatalogManagerPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/payments"
          element={
            <ProtectedRoute>
              <PaymentGatewayPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/orders"
          element={
            <ProtectedRoute>
              <OrderManagementPage />
            </ProtectedRoute>
          }
        />

        {/* Phase 2: Agentic Operations */}
        <Route
          path="/dashboard/agents"
          element={
            <ProtectedRoute>
              <AgentCommandCenterPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/marketplace"
          element={
            <ProtectedRoute>
              <AgentMarketplacePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/inventory"
          element={
            <ProtectedRoute>
              <InventoryManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/fulfillment"
          element={
            <ProtectedRoute>
              <FulfillmentPage />
            </ProtectedRoute>
          }
        />

        {/* Phase 3: Intelligence Layer */}
        <Route
          path="/dashboard/intelligence"
          element={
            <ProtectedRoute>
              <IntelligenceHubPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/memory"
          element={
            <ProtectedRoute>
              <MemoryAnalyticsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/seo"
          element={
            <ProtectedRoute>
              <SEOManagementPage />
            </ProtectedRoute>
          }
        />

        {/* Financial Health Suite (MCA Disruption Engine) */}
        <Route
          path="/dashboard/financial-health"
          element={
            <ProtectedRoute>
              <FinancialHealthPage />
            </ProtectedRoute>
          }
        />

        {/* Phase 4: Scale & Grow */}
        <Route
          path="/dashboard/funding"
          element={
            <ProtectedRoute>
              <FundingDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/global"
          element={
            <ProtectedRoute>
              <GlobalCommercePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/security"
          element={
            <ProtectedRoute>
              <SecurityCompliancePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/channels"
          element={
            <ProtectedRoute>
              <ChannelsHubPage />
            </ProtectedRoute>
          }
        />

        {/* Phase 5: Enterprise Commerce Engines */}
        <Route
          path="/dashboard/orchestration"
          element={
            <ProtectedRoute>
              <OrderOrchestrationPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/wismo"
          element={
            <ProtectedRoute>
              <WISMOPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/agent-work"
          element={
            <ProtectedRoute>
              <AgentWorkHubPage />
            </ProtectedRoute>
          }
        />

        {/* Phase 6: CMO Agent + M2M Lending */}
        <Route
          path="/dashboard/cmo"
          element={
            <ProtectedRoute>
              <CMODashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/merchant-lending"
          element={
            <ProtectedRoute>
              <MerchantLendingPage />
            </ProtectedRoute>
          }
        />

        {/* Chat Settings (Claude API Key) */}
        <Route
          path="/dashboard/settings/chat"
          element={
            <ProtectedRoute>
              <ChatSettingsPage />
            </ProtectedRoute>
          }
        />

        {/* Phase 7: Agent Org Chart */}
        <Route
          path="/dashboard/org-chart"
          element={
            <ProtectedRoute>
              <AgentOrgChartPage />
            </ProtectedRoute>
          }
        />

        {/* Trademark knock-out search (Brand IP department) */}
        <Route
          path="/dashboard/trademark/search"
          element={
            <ProtectedRoute>
              <TrademarkSearchPage />
            </ProtectedRoute>
          }
        />

        {/* Docs graph — wikilink visualization (public for transparency) */}
        <Route path="/docs/graph" element={<DocsGraphPage />} />

        {/* Agent Marketplace (public - for agent access) */}
        <Route path="/marketplace" element={<AgentMarketplacePage />} />
        {/* Agent Work Hub (public - for agent task claiming) */}
        <Route path="/agent-work" element={<AgentWorkHubPage />} />

        {/* Admin Routes */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/super" element={<SuperAdminDashboard />} />
        <Route path="/admin/merchant" element={<MerchantAdminDashboard />} />
        <Route path="/admin/blog" element={<BlogAdmin />} />
        
        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </ErrorBoundary>
    </>
  );
}

export default App;
